import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Partnership logos with actual images
const partnerships = [
  { name: "Swisscare", logo: "https://customer-assets.emergentagent.com/job_eduswitch/artifacts/sf50lquy_Swisscare%20Logo.png" },
  { name: "Duolingo English Test", logo: "https://customer-assets.emergentagent.com/job_eduswitch/artifacts/llm7d881_Duolingo%20Logo.png" },
  { name: "Applyboard", logo: "https://customer-assets.emergentagent.com/job_pathway-finder-7/artifacts/eirpj1su_Applyboard%20Test%20Logo.png" },
  { name: "JRK International", logo: "https://customer-assets.emergentagent.com/job_pathway-finder-7/artifacts/i9ajvla4_JRK%20International%20Educational%20Consultants%20logo.png" },
  { name: "Edunation", logo: "https://customer-assets.emergentagent.com/job_pathway-finder-7/artifacts/3l7uk7ft_Edunation%20Logo%20%281%29.jpg" },
  { name: "Broadway Graduate College", logo: "https://customer-assets.emergentagent.com/job_pathway-finder-7/artifacts/70eve1sf_Banner%20Background%20Removed.png" },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [destinations, setDestinations] = useState([]);
  const [showDestinations, setShowDestinations] = useState(false);
  const [showPartnerships, setShowPartnerships] = useState(false);
  const location = useLocation();
  const destDropdownRef = useRef(null);
  const partnersDropdownRef = useRef(null);

  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        const response = await axios.get(`${API}/destinations`);
        setDestinations(response.data.filter(d => d.is_active));
      } catch (error) {
        console.error("Error fetching destinations:", error);
      }
    };
    fetchDestinations();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (destDropdownRef.current && !destDropdownRef.current.contains(event.target)) {
        setShowDestinations(false);
      }
      if (partnersDropdownRef.current && !partnersDropdownRef.current.contains(event.target)) {
        setShowPartnerships(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Programs", path: "/programs" },
    { name: "Destinations", path: "/destinations", hasDropdown: true },
    { name: "Services", path: "/services" },
    { name: "About", path: "/about" },
    { name: "Partnerships", path: "#", hasPartnersDropdown: true },
    { name: "Testimonials", path: "/testimonials" },
  ];

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md"
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img
              src="/logo.png"
              alt="Sprungly Education"
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <div
                key={link.path + link.name}
                className="relative"
                ref={link.hasDropdown ? destDropdownRef : link.hasPartnersDropdown ? partnersDropdownRef : null}
              >
                {link.hasDropdown ? (
                  <>
                    <button
                      className={`flex items-center gap-1 font-bold transition-colors text-stone-700 hover:text-primary ${
                        showDestinations ? "text-primary" : ""
                      }`}
                      onClick={() => {
                        setShowDestinations(!showDestinations);
                        setShowPartnerships(false);
                      }}
                      data-testid="destinations-dropdown"
                    >
                      {link.name}
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showDestinations ? "rotate-180" : ""}`} />
                    </button>
                    
                    {/* Destinations Dropdown */}
                    {showDestinations && (
                      <div
                        className="absolute top-full left-0 pt-2 w-64 z-50"
                        style={{ zIndex: 9999 }}
                      >
                        <div 
                          className="bg-white rounded-xl shadow-2xl border border-stone-200 py-2 transform origin-top animate-dropdown"
                          data-testid="destinations-menu"
                        >
                          {destinations.length > 0 ? (
                            destinations.map((dest) => (
                              <Link
                                key={dest.country_slug}
                                to={`/destinations/${dest.country_slug}`}
                                className="flex items-center gap-3 px-4 py-3 hover:bg-primary/10 hover:text-primary transition-all duration-200"
                                data-testid={`dest-link-${dest.country_slug}`}
                                onClick={() => setShowDestinations(false)}
                              >
                                <span className="text-xl">{getCountryFlag(dest.country_code)}</span>
                                <span className="font-semibold text-stone-700 hover:text-primary">{dest.country_name}</span>
                              </Link>
                            ))
                          ) : (
                            <div className="px-4 py-3 text-stone-500">Loading destinations...</div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ) : link.hasPartnersDropdown ? (
                  <>
                    <button
                      className={`flex items-center gap-1 font-bold transition-colors text-stone-700 hover:text-primary ${
                        showPartnerships ? "text-primary" : ""
                      }`}
                      onClick={() => {
                        setShowPartnerships(!showPartnerships);
                        setShowDestinations(false);
                      }}
                      data-testid="partnerships-dropdown"
                    >
                      {link.name}
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showPartnerships ? "rotate-180" : ""}`} />
                    </button>
                    
                    {/* Partnerships Dropdown */}
                    {showPartnerships && (
                      <div
                        className="absolute top-full left-1/2 -translate-x-1/2 pt-2 w-72 z-50"
                        style={{ zIndex: 9999 }}
                      >
                        <div 
                          className="bg-white rounded-xl shadow-2xl border border-stone-200 p-4 transform origin-top animate-dropdown"
                          data-testid="partnerships-menu"
                        >
                          <p className="text-xs text-stone-500 uppercase tracking-wider mb-3 font-semibold">Our Partners</p>
                          <div className="grid grid-cols-2 gap-3">
                            {partnerships.map((partner, idx) => (
                              <div
                                key={idx}
                                className="flex items-center justify-center p-3 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors"
                                title={partner.name}
                              >
                                <img 
                                  src={partner.logo} 
                                  alt={partner.name}
                                  className="h-10 w-auto max-w-full object-contain"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={link.path}
                    className={`font-bold transition-colors ${
                      isActive(link.path)
                        ? "text-primary"
                        : "text-stone-700 hover:text-primary"
                    }`}
                    data-testid={`nav-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:+94112555500"
              className="flex items-center gap-2 font-semibold text-stone-700"
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4" />
              <span>+94 011 255 5500</span>
            </a>
            <Link
              to="/contact"
              className="bg-primary hover:bg-primary-600 text-white px-6 py-2.5 rounded-full font-semibold transition-all hover:-translate-y-0.5 shadow-lg shadow-primary/25"
              data-testid="cta-button"
            >
              Free Consultation
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-button"
          >
            {isOpen ? (
              <X className="text-stone-700" />
            ) : (
              <Menu className="text-stone-700" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden bg-white border-t border-stone-100 shadow-xl" data-testid="mobile-menu">
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) =>
              link.hasDropdown ? (
                <div key={link.path + link.name} className="space-y-2">
                  <p className="font-semibold text-stone-900">Destinations</p>
                  <div className="pl-4 space-y-2">
                    {destinations.map((dest) => (
                      <Link
                        key={dest.country_slug}
                        to={`/destinations/${dest.country_slug}`}
                        className="flex items-center gap-2 py-2 text-stone-600"
                        onClick={() => setIsOpen(false)}
                      >
                        <span>{getCountryFlag(dest.country_code)}</span>
                        <span>{dest.country_name}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              ) : link.hasPartnersDropdown ? (
                <div key={link.path + link.name} className="space-y-2">
                  <p className="font-semibold text-stone-900">Partnerships</p>
                  <div className="pl-4 grid grid-cols-2 gap-2">
                    {partnerships.map((partner, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-center p-2 bg-stone-50 rounded-lg"
                        title={partner.name}
                      >
                        <img 
                          src={partner.logo} 
                          alt={partner.name}
                          className="h-8 w-auto max-w-full object-contain"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block py-2 font-bold ${
                    isActive(link.path) ? "text-primary" : "text-stone-700"
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {link.name}
                </Link>
              )
            )}
            <Link
              to="/contact"
              className="block w-full text-center bg-primary text-white py-3 rounded-full font-semibold mt-4"
              onClick={() => setIsOpen(false)}
            >
              Free Consultation
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

// Helper function to get country flag emoji
const getCountryFlag = (countryCode) => {
  const flags = {
    FI: "🇫🇮",
    SE: "🇸🇪",
    GB: "🇬🇧",
    NZ: "🇳🇿",
    IT: "🇮🇹",
    MT: "🇲🇹",
    LV: "🇱🇻",
    KR: "🇰🇷",
    CN: "🇨🇳",
  };
  return flags[countryCode] || "🌍";
};

export default Navbar;
